import { useCallback } from 'react';
import { toast } from '../../../components';
import { useValidateAssociationCodeMutation } from '../../../redux/services/validate-scm-code-service';
import { SUBSCRIPTION_TOAST_IDS } from '../constants';

/**
 * Custom hook to validate billing association codes
 * Encapsulates the validation logic and error handling
 *
 * @returns Object with validateCode function, loading state, and error state
 */
export const useValidateBillingCode = () => {
    const [triggerValidateScmCode, { isLoading: validatingScmCode, error }] = useValidateAssociationCodeMutation();

    /**
     * Validates a billing association code
     *
     * @param email - User's email address
     * @param billingCode - The billing association code to validate
     * @param accountId - AWS account ID
     * @param listing - Optional listing type (CNGFW or AIRFM)
     * @returns Promise<boolean> - true if valid, false if invalid
     */
    const validateCode = useCallback(async (
        email: string,
        billingCode: string,
        accountId: string,
        listing?: 'CNGFW' | 'AIRFM'
    ): Promise<boolean> => {
        try {
            const isValid = await triggerValidateScmCode({
                payload: {
                    UserEmail: email,
                    BillingAccessCode: billingCode,
                    AWSAccountID: accountId,
                    ...(listing && { Listing: listing }),
                }
            }).unwrap();

            return !!isValid;
        } catch (err: any) {
            toast.error(err?.error || 'Failed to validate billing code', {
                toastId: SUBSCRIPTION_TOAST_IDS.VALIDATE_SCM_CODE
            });
            return false;
        }
    }, [triggerValidateScmCode]);

    return {
        validateCode,
        isValidating: validatingScmCode,
        error
    };
};
